import { useRef, useState } from "react";
import { accountService, taskService } from "@miplatform/api";
import {
  ATTEMPT_STATUSES,
  HTTP_STATUSES,
  ISSUE_IMAGE_TYPES,
  ISSUE_STATUSES,
  ISSUE_TYPES,
  VALID_IMAGE_FILE_MIME_TYPES,
} from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { MiTHEME } from "@miplatform/theme";
import {
  createUUID,
  externalReferenceId,
  getDocumentTypeByContentType,
} from "@miplatform/utils";
import { useDocumentsViewModel } from "@miplatform/view-models";
import * as mdi from "@mdi/js";

const ISSUE_RESOLUTION_OPTIONS = {
  REPAIRED: {
    icon: mdi.mdiCheckboxMarked,
    text: "Repair Complete, Return Not Required",
    color: MiTHEME.colors.success.main,
    value: "REPAIR",
  },
  NOT_REPAIRED_NO_RETURN: {
    icon: mdi.mdiCloseBox,
    text: "Repair Incomplete, Return Not Required",
    color: MiTHEME.colors.grey.darkerAccent,
    value: "INCOMPLETE_REPAIR_NO_RETURN",
  },
  NOT_REPAIRED_RETURN: {
    icon: mdi.mdiCloseBox,
    text: "Repair Incomplete, Return Required",
    color: MiTHEME.colors.error.main,
    value: "INCOMPLETE_REPAIR_RETURN",
  },
};

const YES_OR_NO = {
  YES: "Yes",
  NO: "No",
};

const issueStatusResolutionMappings = {
  [ISSUE_RESOLUTION_OPTIONS.REPAIRED.value]: ISSUE_STATUSES.CLOSED,
  [ISSUE_RESOLUTION_OPTIONS.NOT_REPAIRED_NO_RETURN.value]:
    ISSUE_STATUSES.CANCELLED,
  [ISSUE_RESOLUTION_OPTIONS.NOT_REPAIRED_RETURN.value]:
    ISSUE_STATUSES.CONFIRMED,
};

export const useClosingoutViewModel = ({
  orderTemplateId,
  getAttempt = () => {},
}) => {
  const stateManager = useComponentState();
  const pendingDeletionDocumentsRef = useRef([]);

  const [invoice, setInvoice] = useState(null);

  const { uploadDocument, removeDocument } = useDocumentsViewModel();

  const [issueImages, setIssueImages] = useState([]);
  const [repairImages, setRepairImages] = useState([]);
  const [imagesInViewer, setImagesInViewer] = useState([]);
  const [showImagesViewer, setShowImagesViewer] = useState(false);
  const [issue, setIssue] = useState();

  const [issueTemplates, setIssueTemplates] = useState([]);

  const getIssue = async ({ id }) => {
    const res = await taskService.issues.getById({ id });

    if (res.status === HTTP_STATUSES.OK) {
      setIssue(res.data);
    } else {
      setIssue(undefined);
    }
  };

  const COMPLETED_STATUSES = [
    ISSUE_RESOLUTION_OPTIONS.NOT_REPAIRED_NO_RETURN.value,
    ISSUE_RESOLUTION_OPTIONS.REPAIRED.value,
  ];

  const getInvoice = (invoiceId) => {
    stateManager.run(async () => {
      const { status, data = [] } = await accountService.invoices.getById({
        id: invoiceId,
      });

      if (status === HTTP_STATUSES.OK) {
        setInvoice(data);
      }
    });
  };

  const uploadImages = async ({ issueId, issueResultExternalReferenceId }) => {
    await Promise.all([
      ...issueImages
        .filter((x) => !x.id)
        .map(({ file }) =>
          uploadDocument({
            file,
            documentType: getDocumentTypeByContentType(file.type),
            externalData: JSON.stringify({
              imageType: ISSUE_IMAGE_TYPES.ORIGINATING,
            }),
            externalReferenceId: externalReferenceId.getIssueRefId({
              issueId,
            }),
            additionalExternalReferences: [issueResultExternalReferenceId].join(
              ","
            ),
          })
        ),
      ...repairImages
        .filter((x) => !x.id)
        .map(({ file }) =>
          uploadDocument({
            file,
            documentType: getDocumentTypeByContentType(file.type),
            externalData: JSON.stringify({
              imageType: ISSUE_IMAGE_TYPES.RESOLVING,
            }),
            externalReferenceId: externalReferenceId.getIssueRefId({
              issueId,
            }),
            additionalExternalReferences: [issueResultExternalReferenceId].join(
              ","
            ),
          })
        ),
    ]);
  };

  const handleIssueStatusUpdate = async (
    status,
    issue,
    repairDetails,
    isIssueBillable
  ) => {
    const toastMessageConfig = {
      success: {
        text: `The task was updated successfully!`,
      },
    };

    if (status === ISSUE_RESOLUTION_OPTIONS.REPAIRED.value) {
      return taskService.issues.resolve({
        id: issue.id,
        data: {
          repairDescription: repairDetails,
          isBillable: isIssueBillable === YES_OR_NO.YES,
          modifiedOn: issue.modifiedOn,
        },
        toastMessageConfig,
      });
    }
    if (status === ISSUE_RESOLUTION_OPTIONS.NOT_REPAIRED_NO_RETURN.value) {
      return taskService.issues.cancel({
        id: issue.id,
        data: { modifiedOn: issue.modifiedOn },
        toastMessageConfig,
      });
    }
    if (status === ISSUE_RESOLUTION_OPTIONS.NOT_REPAIRED_RETURN.value) {
      return taskService.issues.confirm({
        id: issue.id,
        data: { modifiedOn: issue.modifiedOn },
      });
    }

    return null;
  };

  const reviewIssue = ({
    attemptId,
    attemptStatus,
    assignmentId,
    userId,
    issue,
    issueDescription,
    diagnosedIssue,
    repairStatus,
    repairDetails,
    isIssueBillable,
    orderId,
    issueTemplateId,
    areaName,
    originalDescription,
  }) => {
    return stateManager.run(async () => {
      const issueResultId = issue.issueResult
        ? issue.issueResult.id
        : createUUID();

      const { data: issueResult, status } =
        await taskService.issuesResults.upsertIssueResult({
          data: {
            id: issueResultId,
            issueId: issue.id,
            attemptId,
            description: issueDescription,
            repairDescription: repairDetails,
            title: issue.title,
            type: ISSUE_TYPES.RECORDABLE,
            areaName: areaName || issue.areaName,
            status: issueStatusResolutionMappings[repairStatus],
            diagnosedIssue,
            assignmentId,
            issueTemplateId,
            originalDescription,
            submittedByUserId: userId,
          },
        });

      if (status === HTTP_STATUSES.OK) {
        getAttempt();
      }

      if (attemptStatus === ATTEMPT_STATUSES.CLOSEOUT_IN_PROGRESS) {
        await handleIssueStatusUpdate(
          repairStatus,
          issue,
          repairDetails,
          isIssueBillable
        );
      }

      Promise.all([
        ...pendingDeletionDocumentsRef.current.map((document) =>
          removeDocument(document)
        ),
        uploadImages({
          issueId: issue.id,
          issueResultExternalReferenceId:
            externalReferenceId.getIssueResultRefId({
              orderId,
              attemptId: issueResult.attemptId,
              issueId: issueResult.issueId,
              assignmentId,
              issueResultId: issueResult.id,
            }),
        }),
      ]);
    });
  };

  const validateFile = (file, maxFileSizeInMB = 30) => {
    const newFile = file;
    file.error = null;

    if (file.size > maxFileSizeInMB * 10 * 1024 * 1024) {
      file.error = `File must be less than ${maxFileSizeInMB}MB.`;
    }

    if (!VALID_IMAGE_FILE_MIME_TYPES.includes(file.type)) {
      file.error = "File is the wrong format.";
    }

    return {
      file: newFile,
      tempURL: URL.createObjectURL(newFile),
      contentType: file.type,
    };
  };

  const handleAddIssueImage = (e) => {
    e.preventDefault();
    const { files } = e.dataTransfer || e.target;

    Object.values(files)?.forEach((file) => {
      setIssueImages((prev) => [...prev, validateFile(file)]);
    });
  };

  const handleAddRepairImage = (e) => {
    e.preventDefault();
    const { files } = e.dataTransfer || e.target;

    Object.values(files)?.forEach((file) => {
      setRepairImages((prev) => [...prev, validateFile(file)]);
    });
  };

  const removeIssueImage = (document, index) => {
    if (document.id) {
      pendingDeletionDocumentsRef.current.push(document);
    }
    const filteredIssueImages = issueImages.filter((_, i) => i !== index);
    setIssueImages(filteredIssueImages);
    setImagesInViewer(filteredIssueImages);
    if (filteredIssueImages.length === 0) {
      setShowImagesViewer(false);
    }
  };

  const removeRepairImage = (document, index) => {
    if (document.id) {
      pendingDeletionDocumentsRef.current.push(document);
    }
    const filteredRepairImages = repairImages.filter((_, i) => i !== index);
    setRepairImages(filteredRepairImages);
    setImagesInViewer(filteredRepairImages);
    if (filteredRepairImages.length === 0) {
      setShowImagesViewer(false);
    }
  };

  const filterIssueFiles = (documents) => {
    const resolvingDocuments = [];
    const originatedDocuments = [];

    documents?.forEach((image) => {
      const externalData = JSON.parse(image.externalData);

      if (externalData.imageType === ISSUE_IMAGE_TYPES.ORIGINATING) {
        originatedDocuments.push(image);
      }

      if (externalData.imageType === ISSUE_IMAGE_TYPES.RESOLVING) {
        resolvingDocuments.push(image);
      }
    });

    return { resolvingDocuments, originatedDocuments };
  };

  const isStatusRepair = (status) => {
    return status === ISSUE_RESOLUTION_OPTIONS.REPAIRED.value || !status;
  };

  const getTaskResolutionStatus = (issue) => {
    const status = issue.issueResult?.status ?? issue.status;

    let resolutionStatus = null;
    Object.keys(issueStatusResolutionMappings)?.forEach((mapKey) => {
      if (issueStatusResolutionMappings[mapKey] === status) {
        resolutionStatus = mapKey;
      }
    });

    return resolutionStatus;
  };

  const getIssueTemplates = () =>
    stateManager.run(async () => {
      const res = await taskService.orderTemplates.getById({
        id: orderTemplateId,
      });

      if (res.status === HTTP_STATUSES.OK) {
        setIssueTemplates(res.data.issueTemplates);
      }
    });

  return {
    ISSUE_RESOLUTION_OPTIONS,
    issueImages,
    repairImages,
    showImagesViewer,
    imagesInViewer,
    setImagesInViewer,
    setShowImagesViewer,
    setIssueImages,
    setRepairImages,
    handleAddIssueImage,
    handleAddRepairImage,
    removeIssueImage,
    removeRepairImage,
    filterIssueFiles,
    isStatusRepair,
    COMPLETED_STATUSES,
    invoice,
    getInvoice,
    reviewIssue,
    getTaskResolutionStatus,
    setInvoice,
    issueTemplates,
    getIssueTemplates,
    getIssue,
    issue,
  };
};
